import { Check, Circle, Lock } from 'lucide-react';
import { HexAccent } from './HexMotif';

/**
 * Persistent onboarding journey visualization
 * Stages: Applied → Approved → Signed → Onboarding Call → Active
 */
const STAGES = [
  { key: 'applied', label: 'Applied' },
  { key: 'approved', label: 'Approved' },
  { key: 'signed', label: 'Documents Signed' },
  { key: 'onboarded', label: 'Onboarding Call' },
  { key: 'active', label: 'Active Carrier' },
];

/**
 * @param {object} props
 * @param {string} props.currentStage - One of 'applied' | 'approved' | 'signed' | 'onboarded' | 'active'
 * @param {boolean} [props.dismissible=false] - Show dismiss after active
 * @param {function} [props.onDismiss]
 */
const OnboardingProgressMap = ({ currentStage = 'signed', dismissible = false, onDismiss }) => {
  const currentIndex = STAGES.findIndex(s => s.key === currentStage);

  if (currentStage === 'active' && !dismissible) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-[#0D0D0D] via-[#121212] to-[#0D0D0D] border border-[#D4AF37]/20 p-4 sm:p-5 mb-6 overflow-x-auto"
      data-testid="onboarding-progress-map"
    >
      <div className="flex items-center justify-between mb-3 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <HexAccent size={18} color="#D4AF37" className="flex-shrink-0" />
          <h3 className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest truncate">
            Your Hive Journey
          </h3>
        </div>
        {dismissible && (
          <button
            onClick={onDismiss}
            className="text-white/40 hover:text-white/70 text-xs uppercase tracking-wider flex-shrink-0"
          >
            Dismiss
          </button>
        )}
      </div>

      {/* Horizontal stepper */}
      <div className="relative flex items-start justify-between min-w-[320px]">
        {/* Connector line */}
        <div className="absolute top-4 left-0 right-0 h-px bg-white/10" />
        <div
          className="absolute top-4 left-0 h-px bg-[#D4AF37] transition-all duration-500"
          style={{
            width: currentIndex >= 0 ? `${(currentIndex / (STAGES.length - 1)) * 100}%` : '0%',
          }}
        />

        {STAGES.map((stage, idx) => {
          const isCompleted = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          const isPending = idx > currentIndex;

          return (
            <div
              key={stage.key}
              className="relative z-10 flex flex-col items-center gap-2"
              style={{ flex: '1' }}
            >
              <div
                className={`w-8 h-8 flex items-center justify-center border-2 transition-colors ${
                  isCompleted
                    ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                    : isCurrent
                    ? 'bg-[#0A0A0A] border-[#D4AF37] text-[#D4AF37] animate-pulse'
                    : 'bg-[#0A0A0A] border-white/20 text-white/30'
                }`}
                style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" strokeWidth={3} />
                ) : isCurrent ? (
                  <Circle className="w-3 h-3 fill-current" />
                ) : (
                  <Lock className="w-3 h-3" />
                )}
              </div>
              <span
                className={`text-[10px] sm:text-xs uppercase tracking-wider text-center max-w-[80px] leading-tight ${
                  isCurrent
                    ? 'text-[#D4AF37] font-bold'
                    : isCompleted
                    ? 'text-white/80'
                    : 'text-white/30'
                }`}
              >
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnboardingProgressMap;
